import React, { useEffect, useState } from 'react';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import Layer from 'structure/Layer.js';
import MultipleTextField from 'views/MultipleTextField.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

const AddEditUserForSelection = ({ config, isNewTarget, onChange, target }, { index, options, utils }) => {

    const layerID = isNewTarget ? 'new_user_selection' : `edit_user_selection_${target.id}`;
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);

    const onDoneClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // prepare payload for subscribers and add an id if an id has not been generated
            let payload = {
                id: Utils.randomString(),
                ...edits
            }

            // close layer and notify subscribers of data change
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(payload);
            }

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue ${isNewTarget ? 'submitting' : 'updating'} your user details. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_user_for_selection.on_done_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'done',
            onClick: onDoneClick,
            text: 'Done'
        }];
    }

    const getFields = () => {
        
        // prepare default sections
        let sections = [{
            collapsed: false,
            key: 'details',
            lastItem: false,
            title: 'Name and Contact Information',
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number
            }]
        }];

        // determine if custom sections were provided
        if(config?.components) {
            config.components.forEach(entry => {
                sections[0].items.push({
                    component: entry.component?.key,
                    key: entry.id,
                    onChange: val => onUpdateTarget({ [entry.id]: val }),
                    title: entry.title,
                    value: edits[entry.id]
                })
            })
        }
        return sections;
    }

    const setupEditing = async () => {
        setEdits({ ...target });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

const ContactPicker = ({ config, onChange, utils, value }) => {

    const [values, setValues] = useState([]);

    const onExistingContactClick = contact => {
        utils.layer.open({
            Component: AddEditUserForSelection.bind(this, { 
                config,
                isNewTarget: false,
                onChange: result => {

                    // update internal state for selected values
                    let next = values.map(item => item.id === contact.id ? result : item);
                    setValues(next);

                    // notify subscribers of data change
                    if(typeof(onChange) === 'function') {
                        onChange(next);
                    }
                },
                target: contact 
            }),
            id: `edit_user_selection_${contact.id}`
        });
    }

    const onNewContactClick = () => {
        utils.layer.open({
            Component: AddEditUserForSelection.bind(this, { 
                config,
                isNewTarget: true,
                onChange: result => {

                    // update internal state for selected values
                    let next = values.concat([result]);
                    setValues(next);

                    // notify subscribers of data change
                    if(typeof(onChange) === 'function') {
                        onChange(next);
                    }
                }
            }),
            id: 'new_user_selection'
        });
    }

    useEffect(() => {
        setValues(Array.isArray(value) ? value : []);
    }, [value]);
    
    return (
        <MultipleTextField
        onChange={onChange}
        onClick={onNewContactClick}
        placeholder={'Click to configure a contact...'}
        onRenderItem={(item, index, items, { onRemoveItem }) => {
            return (
                <div key={index}>
                    {Views.row({
                        bottomBorder: index !== items.length - 1,
                        hideOnClickArrow: true,
                        label: `${item.first_name} ${item.last_name}`,
                        onClick: onExistingContactClick.bind(this, item),
                        value: (
                            <img
                            className={'text-button'}
                            onClick={onRemoveItem.bind(this, index)}
                            src={'images/delete-red-icon.png'}
                            style={{
                                height: 20,
                                objectFit: 'contain',
                                width: 20
                            }} />
                        )
                    })}
                </div>
            )
        }}
        value={values} />
    )
}

export default ContactPicker;
